// src/pages/timetable/StudentTimetable.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AcademicCapIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { FireLoader } from "../../components/Loader";

const WEEK_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DEFAULT_SCHOOL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

const errorMessage = (error) =>
  error?.response?.data?.message ||
  "Unable to load your timetable. Please try again.";

const teacherName = (teacher) =>
  [teacher?.first_name, teacher?.last_name].filter(Boolean).join(" ");

const formatTime = (value) =>
  value ? String(value).slice(0, 5) : "--:--";

const isEnabled = (value) =>
  value === true || value === 1 || value === "1";

const isTeachingPeriod = (period) =>
  isEnabled(period?.is_teaching_period) &&
  String(period?.period_type || "lesson").toLowerCase() === "lesson";

export default function StudentTimetable() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timetable, setTimetable] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [teachingDays, setTeachingDays] = useState(DEFAULT_SCHOOL_DAYS);

  const loadTimetable = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      // This endpoint resolves the authenticated student on the backend and
      // returns only the timetable matching the student's current_class_id.
      const [timetableResponse, periodsResponse, settingsResponse] =
        await Promise.all([
          apiClient.get("/timetables/my-class"),
          apiClient.get("/timetables/periods"),
          apiClient.get("/timetables/settings"),
        ]);

      const timetableData = timetableResponse?.data?.data || null;
      const periodRows = Array.isArray(periodsResponse?.data?.data)
        ? periodsResponse.data.data
        : [];
      const configuredDays = settingsResponse?.data?.data?.teaching_days;
      const orderedDays =
        Array.isArray(configuredDays) && configuredDays.length
          ? WEEK_DAYS.filter((day) => configuredDays.includes(day))
          : DEFAULT_SCHOOL_DAYS;

      setTimetable(timetableData);
      setPeriods(periodRows);
      setTeachingDays(orderedDays);
    } catch (requestError) {
      console.error("Failed to load student timetable:", requestError);
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTimetable();
  }, [loadTimetable]);

  const entries = useMemo(
    () => (Array.isArray(timetable?.entries) ? timetable.entries : []),
    [timetable],
  );

  const sortedPeriods = useMemo(
    () =>
      periods
        .filter((period) => period?.is_active !== false)
        .sort(
          (first, second) =>
            Number(first.period_number) - Number(second.period_number),
        ),
    [periods],
  );

  const entriesBySlot = useMemo(() => {
    const slots = new Map();
    entries.forEach((entry) => {
      if (teachingDays.includes(entry.day_of_week)) {
        slots.set(`${entry.day_of_week}:${entry.period_id}`, entry);
      }
    });
    return slots;
  }, [entries, teachingDays]);

  const getEntry = (day, periodId) =>
    entriesBySlot.get(`${day}:${periodId}`);

  const scheduledLessons = useMemo(
    () =>
      entries.filter(
        (entry) =>
          teachingDays.includes(entry.day_of_week) && entry.subject_id,
      ).length,
    [entries, teachingDays],
  );

  const className = timetable?.class?.name || timetable?.title || "My Class";
  const academicYear = timetable?.academicYear?.name || "Current academic year";

  if (loading) {
    return <FireLoader fullScreen size="lg" text="Loading your timetable..." />;
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-red-100 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <CalendarDaysIcon className="h-6 w-6 text-red-500" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-gray-900">
          Timetable unavailable
        </h2>
        <p className="mt-2 text-sm text-red-600">{error}</p>
        <button
          type="button"
          onClick={loadTimetable}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Try again
        </button>
      </div>
    );
  }

  if (!timetable) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center shadow-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50">
          <CalendarDaysIcon className="h-8 w-8 text-primary-400" />
        </div>
        <h2 className="mt-5 text-xl font-bold text-gray-800">
          No timetable available
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-500">
          Your school has not published a timetable for your current class and
          academic year yet.
        </p>
      </div>
    );
  }

  const LessonCard = ({ entry, period, compact = false }) => {
    if (!isTeachingPeriod(period)) {
      return (
        <div className="flex min-h-[86px] items-center justify-center rounded-xl border border-amber-100 bg-amber-50 p-3 text-center text-xs font-bold capitalize text-amber-700">
          {period.name}
        </div>
      );
    }

    if (!entry) {
      return (
        <div className="flex min-h-[86px] items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/60 text-xs font-medium text-gray-400">
          Free period
        </div>
      );
    }

    return (
      <div
        className={`rounded-xl border border-primary-100 bg-gradient-to-br from-primary-50 to-white p-3 shadow-sm ${
          compact ? "min-h-0" : "min-h-[86px]"
        }`}
      >
        <div className="font-bold text-gray-900">
          {entry.subject?.name || "Lesson"}
        </div>
        <div className="mt-2 flex items-start gap-1.5 text-gray-600">
          <UserIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-500" />
          <span>{teacherName(entry.teacher) || "Teacher not assigned"}</span>
        </div>
        {entry.room && (
          <div className="mt-1 flex items-center gap-1.5 text-gray-400">
            <MapPinIcon className="h-3.5 w-3.5" />
            <span>{entry.room}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-8">
      <section className="overflow-hidden rounded-2xl bg-gradient-to-r from-primary-700 via-primary-600 to-blue-500 p-6 text-white shadow-lg sm:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-primary-100">
              <CheckBadgeIcon className="h-5 w-5" />
              Student class timetable
            </div>
            <h1 className="mt-2 text-2xl font-bold sm:text-3xl">My Timetable</h1>
            <p className="mt-2 max-w-xl text-sm text-primary-100 sm:text-base">
              Your complete weekly schedule for {className}.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:min-w-[380px]">
            <div className="rounded-xl bg-white/15 p-4 backdrop-blur-sm">
              <AcademicCapIcon className="h-5 w-5 text-primary-100" />
              <p className="mt-2 text-xs text-primary-100">Class</p>
              <p className="truncate font-bold">{className}</p>
            </div>
            <div className="rounded-xl bg-white/15 p-4 backdrop-blur-sm">
              <CalendarDaysIcon className="h-5 w-5 text-primary-100" />
              <p className="mt-2 text-xs text-primary-100">Academic year</p>
              <p className="truncate font-bold">{academicYear}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Teaching days
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {teachingDays.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Weekly lessons
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {scheduledLessons}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Periods per day
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {sortedPeriods.length}
          </p>
        </div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 z-30 min-w-[130px] bg-gray-50 px-4 py-4 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                  Day / Period
                </th>
                {sortedPeriods.map((period) => (
                  <th
                    key={period.id}
                    className="min-w-[170px] bg-gray-50 px-3 py-4 text-center"
                  >
                    <div className="text-sm font-bold text-gray-700">
                      {period.name}
                    </div>
                    <div className="mt-1 flex items-center justify-center gap-1 text-xs font-medium text-gray-400">
                      <ClockIcon className="h-3.5 w-3.5" />
                      {formatTime(period.start_time)}–{formatTime(period.end_time)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teachingDays.map((day) => (
                <tr key={day} className="border-t border-gray-100">
                  <td className="sticky left-0 z-20 whitespace-nowrap border-r border-gray-100 bg-white px-4 py-4">
                    <div className="text-sm font-bold text-gray-800">{day}</div>
                  </td>
                  {sortedPeriods.map((period) => (
                    <td key={`${day}-${period.id}`} className="p-2 align-top">
                      <LessonCard
                        entry={getEntry(day, period.id)}
                        period={period}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {!sortedPeriods.length && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 text-sm font-medium text-amber-700">
          Your school has not configured timetable periods yet.
        </div>
      )}
    </div>
  );
}